// useWorkspaceData — single React hook the Workspace UI consumes.
// Handles project + conversation state, refresh, and all CRUD calls,
// abstracting Supabase vs localStorage.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  listProjects, createProject, renameProject, deleteProject,
  updateProjectKnowledge,
  listConversations, createConversation, renameConversation,
  moveConversation, deleteConversation, touchConversation,
} from "@/lib/workspaceData";

const PROJECT_COLLAPSED_KEY = "replyai.projectCollapsed.v1";

function loadCollapsedMap() {
  try { return JSON.parse(localStorage.getItem(PROJECT_COLLAPSED_KEY) || "{}"); }
  catch { return {}; }
}
function saveCollapsedMap(m) {
  try { localStorage.setItem(PROJECT_COLLAPSED_KEY, JSON.stringify(m)); } catch { /* ignore */ }
}

export function useWorkspaceData() {
  const { user, loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedMap, setCollapsedMap] = useState(loadCollapsedMap);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [p, c] = await Promise.all([
        listProjects(user),
        listConversations(user),
      ]);
      setProjects(p);
      setConversations(c);
    } catch (e) {
      setError(e.message || "Failed to load workspace");
      console.error("[workspace] refresh failed:", e);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return; // wait for auth to settle so we know which backend to use
    setLoading(true);
    refresh();
  }, [authLoading, user?.id, refresh]);

  // ---- Project mutations ----
  const addProject = useCallback(async (name) => {
    const p = await createProject(user, name);
    setProjects((prev) => [p, ...prev]);
    return p;
  }, [user]);

  const updateProject = useCallback(async (id, name) => {
    const p = await renameProject(user, id, name);
    if (p) setProjects((prev) => prev.map((x) => (x.id === id ? p : x)));
    return p;
  }, [user]);

  const removeProject = useCallback(async (id) => {
    await deleteProject(user, id);
    setProjects((prev) => prev.filter((p) => p.id !== id));
    // Un-assign conversations locally for immediate UI update
    setConversations((prev) =>
      prev.map((c) => (c.project_id === id ? { ...c, project_id: null } : c))
    );
  }, [user]);

  const updateKnowledge = useCallback(async (id, knowledge) => {
    const p = await updateProjectKnowledge(user, id, knowledge);
    if (p) setProjects((prev) => prev.map((x) => (x.id === id ? p : x)));
    return p;
  }, [user]);

  const toggleProjectCollapsed = useCallback((id) => {
    setCollapsedMap((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      saveCollapsedMap(next);
      return next;
    });
  }, []);

  // ---- Conversation mutations ----
  const addConversation = useCallback(async (opts = {}) => {
    const c = await createConversation(user, opts);
    setConversations((prev) => [c, ...prev]);
    return c;
  }, [user]);

  const updateConversation = useCallback(async (id, title) => {
    const c = await renameConversation(user, id, title);
    if (c) setConversations((prev) => prev.map((x) => (x.id === id ? c : x)));
    return c;
  }, [user]);

  const moveConversationToProject = useCallback(async (id, projectId) => {
    const c = await moveConversation(user, id, projectId);
    if (c) setConversations((prev) => prev.map((x) => (x.id === id ? c : x)));
    return c;
  }, [user]);

  const removeConversation = useCallback(async (id) => {
    await deleteConversation(user, id);
    setConversations((prev) => prev.filter((c) => c.id !== id));
  }, [user]);

  const touch = useCallback(async (id, patch) => {
    await touchConversation(user, id, patch);
    // Optimistic local update
    setConversations((prev) =>
      prev.map((c) =>
        c.id === id ? { ...c, ...patch, updated_at: new Date().toISOString() } : c
      ).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
    );
  }, [user]);

  // ---- Derived selectors ----
  const conversationsByProject = useMemo(() => {
    const map = {};
    for (const c of conversations) {
      if (c.project_id) {
        (map[c.project_id] = map[c.project_id] || []).push(c);
      }
    }
    return map;
  }, [conversations]);

  const orphanConversations = useMemo(
    () => conversations.filter((c) => !c.project_id),
    [conversations]
  );

  return {
    user,
    loading: loading || authLoading,
    error,
    projects,
    conversations,
    conversationsByProject,
    orphanConversations,
    collapsedMap,
    toggleProjectCollapsed,
    refresh,
    addProject,
    updateProject,
    updateKnowledge,
    removeProject,
    addConversation,
    updateConversation,
    moveConversationToProject,
    removeConversation,
    touch,
  };
}
